import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import SignaturePad from 'react-signature-pad-wrapper';
import clsx from 'clsx';
import {useCombinedRef} from '@chekin/core';
import {useFrame} from '../../../context/frame';
import {DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {
  ClearSignatureButton,
  SignCanvas,
  SignPlaceholder,
  SignPlaceholderText,
  SignWrapper,
} from './styled';

const SIGNATURE_PROPS = {
  penColor: '#161643',
  minWidth: 1.5,
  maxWidth: 1.5,
  dotSize: 1,
};

type SignatureCanvasProps = {
  onClear?: () => void;
  onEnable?: () => void;
  hasSignature?: boolean;
  onEnd?: () => void;
  onStart?: () => void;
  className?: string;
  enabled?: boolean;
};

const SignatureCanvas = React.forwardRef<SignaturePad, SignatureCanvasProps>(
  (
    {
      onClear = () => {},
      onEnd = () => {},
      onEnable = () => {},
      hasSignature,
      className,
      enabled,
    },
    ref,
  ) => {
    const {t} = useTranslation();
    const internalRef = useRef<SignaturePad | null>(null);
    const combinedInputRef = useCombinedRef(ref, internalRef);

    const {isMatch: isMobile} = useScreenResize(DEVICE.laptop);
    const {window} = useFrame();
    const canvasSize = {
      width: window.innerHeight < 600 ? 319 : 646,
      height: window.innerHeight < 600 ? 182 : 302,
    };
    const currentCanvasSize: Record<string, string> = {
      width: isMobile ? '100%' : canvasSize.width + 'px',
      height: isMobile ? '100%' : canvasSize.height + 'px',
    };

    const enableSignature = () => {
      if (onEnable) {
        onEnable();
      }
    };

    React.useEffect(() => {
      if (!onEnd) return;
      const signPad = internalRef.current?.instance;
      signPad?.addEventListener('endStroke', onEnd);

      return () => {
        signPad?.removeEventListener('endStroke', onEnd);
      };
    }, [onEnd]);

    return (
      <SignWrapper className={clsx(className, 'signature')}>
        {!enabled && (
          <SignPlaceholder
            data-testid="signature-placeholder"
            onClick={enableSignature}
            className="signature__placeholder"
          >
            <SignPlaceholderText>{t('tap_here_to_sign')}</SignPlaceholderText>
          </SignPlaceholder>
        )}
        <SignCanvas hidden={!enabled} className="signature__canvas">
          <SignaturePad
            ref={combinedInputRef}
            data-testid="canvas"
            options={SIGNATURE_PROPS}
            canvasProps={{
              style: {
                width: currentCanvasSize.width,
                height: currentCanvasSize.height,
              },
            }}
          />
          {hasSignature && (
            <ClearSignatureButton onClick={onClear} className="signature__clear-btn">
              {t('clear')}
            </ClearSignatureButton>
          )}
        </SignCanvas>
      </SignWrapper>
    );
  },
);

export {SignatureCanvas};
